import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { List, VerticalTabs, VerticalTabsButton, VerticalTabsList, VerticalTabsPanel, Text, TextFontSizes, TextFontWeights, TextLineHeights, Tip, VerticalTabsTitle } from "@cinch-labs/design-system";
import { Wrapper } from "@cinch-nx/shared-ui";
import { interpolateI18n } from "@cinch-labs/shared-util";
import styles from "./understand-car-finance.module.css";
import Link from "next/link";
import { useAnalytics } from "@cinch-nx/shared-analytics";
import { calculatorHpExplainedSelected, calculatorPcpExplainedSelected, understandingFinanceClick } from "../../tracking/tracking-events";
function renderBoldP(text) {
    return /*#__PURE__*/ _jsx(Text, {
        fontWeight: TextFontWeights.bold,
        fontSize: TextFontSizes.l,
        className: styles.boldText,
        lineHeight: TextLineHeights.display,
        children: text
    }, text);
}
function renderP(text) {
    return /*#__PURE__*/ _jsx(Text, {
        fontSize: TextFontSizes.m,
        fontWeight: TextFontWeights.book,
        className: styles.text,
        children: text
    }, text);
}
function renderH4(text) {
    return /*#__PURE__*/ _jsx(Text, {
        element: "h4",
        fontWeight: TextFontWeights.bold,
        lineHeight: TextLineHeights.display,
        fontSize: TextFontSizes.l,
        className: styles.smallTitle,
        children: text
    }, text);
}
function renderTip(text) {
    const content = text.split("<split>");
    if (content.length > 1) {
        return /*#__PURE__*/ _jsx(Tip, {
            titleEl: "p",
            title: content[0],
            text: content[1],
            classNames: {
                container: styles.info
            }
        }, text);
    }
    return /*#__PURE__*/ _jsx(Tip, {
        text: content[0],
        classNames: {
            container: styles.info
        }
    }, text);
}
function renderList(list) {
    const items = list.split("<split>");
    return /*#__PURE__*/ _jsx(List, {
        items: items,
        textProps: {
            fontSize: TextFontSizes.m
        },
        classNames: {
            list: styles.list,
            item: styles.items
        }
    }, list);
}
function renderBoldAndNormalText(text) {
    const content = text.split("<bold>");
    if (content.length > 1) {
        return /*#__PURE__*/ _jsxs("p", {
            children: [
                /*#__PURE__*/ _jsx(Text, {
                    element: "span",
                    fontSize: TextFontSizes.m,
                    fontWeight: TextFontWeights.bold,
                    className: styles.boldText,
                    children: content[0]
                }),
                /*#__PURE__*/ _jsx(Text, {
                    element: "span",
                    fontSize: TextFontSizes.m,
                    className: styles.text,
                    children: content[1]
                })
            ]
        }, text);
    }
    return renderP(text);
}
function UnderstandingCarFinance({ i18n  }) {
    const { title , sections , ariaLabel  } = i18n;
    const { trackEvent  } = useAnalytics();
    const rteTags = {
        list: renderList,
        info: renderTip,
        h4: renderH4,
        p: renderP,
        boldP: renderBoldP,
        pcpLink: renderPcpLink,
        hpLink: renderHpLink,
        boldAndNormalText: renderBoldAndNormalText
    };
    function renderPcpLink(text) {
        return /*#__PURE__*/ _jsx(Text, {
            className: styles.linkText,
            element: "p",
            children: /*#__PURE__*/ _jsx(Link, {
                href: "car-finance/personal-contract-purchase",
                passHref: true,
                legacyBehavior: true,
                children: /*#__PURE__*/ _jsx("a", {
                    target: "_blank",
                    rel: "noreferrer noopener",
                    href: "car-finance/personal-contract-purchase",
                    onClick: ()=>trackEvent(calculatorPcpExplainedSelected()),
                    children: text
                })
            })
        }, text);
    }
    function renderHpLink(text) {
        return /*#__PURE__*/ _jsx(Text, {
            className: styles.linkText,
            element: "p",
            children: /*#__PURE__*/ _jsx(Link, {
                href: "/car-finance/hire-purchase",
                passHref: true,
                legacyBehavior: true,
                children: /*#__PURE__*/ _jsx("a", {
                    target: "_blank",
                    rel: "noreferrer noopener",
                    href: "/car-finance/hire-purchase",
                    onClick: ()=>trackEvent(calculatorHpExplainedSelected()),
                    children: text
                })
            })
        }, text);
    }
    function renderRte(content) {
        return /*#__PURE__*/ _jsx("div", {
            className: styles.stack,
            children: interpolateI18n(content, undefined, rteTags)
        });
    }
    return /*#__PURE__*/ _jsx("article", {
        className: styles.understandingFinance,
        children: /*#__PURE__*/ _jsx(Wrapper, {
            children: /*#__PURE__*/ _jsxs("div", {
                "aria-labelledby": "understand-car-finance-title",
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        element: "h2",
                        fontSize: TextFontSizes["4xl"],
                        fontWeight: TextFontWeights.bold,
                        lineHeight: TextLineHeights.display,
                        className: styles.understandingFinanceTitle,
                        id: "understand-car-finance-title",
                        children: title
                    }),
                    /*#__PURE__*/ _jsxs(VerticalTabs, {
                        className: styles.understandingFinanceVerticalTabs,
                        children: [
                            /*#__PURE__*/ _jsx(VerticalTabsList, {
                                ariaLabel: ariaLabel,
                                children: sections.map((section, index)=>/*#__PURE__*/ _jsx(VerticalTabsButton, {
                                        id: `button-${index}`,
                                        panelId: `understanding-finance-${index}`,
                                        onActive: ()=>{
                                            trackEvent(understandingFinanceClick(title, section.title));
                                        },
                                        children: section.title
                                    }, `button-${index}`))
                            }),
                            sections.map((section, index)=>/*#__PURE__*/ _jsxs(VerticalTabsPanel, {
                                    id: `understanding-finance-${index}`,
                                    buttonId: `button-${index}`,
                                    children: [
                                        /*#__PURE__*/ _jsx(VerticalTabsTitle, {
                                            children: section.title
                                        }),
                                        renderRte(section.content)
                                    ]
                                }, `understanding-finance-${index}`))
                        ]
                    })
                ]
            })
        })
    });
}
export default UnderstandingCarFinance;
